import { FC, FormEvent, useEffect, useState } from 'react';

import { router, useForm, usePage } from '@inertiajs/react';

import { DeleteProfileForm } from './DeleteProfileForm';
import {
    DeliveryProfilesForm,
    SavedDeliveryProfile,
} from './DeliveryProfilesForm';
import {
    NotificationSetting,
    NotificationSettingsForm,
} from './NotificationSettingsForm';
import { PasswordChangeForm } from './PasswordChangeForm';
import { PersonalDataForm } from './PersonalDataForm';
import { PreferredUniverseForm } from './PreferredUniverseForm';
import { SellerAvailabilityForm } from './SellerAvailabilityForm';
import { ShippingMethodsForm } from './ShippingMethodsForm';
import {
    TelegramBotSettings,
    TelegramBotSettingsData,
} from './TelegramBotSettings';
import { Button } from '../../components/ui/Button';
import { Routes } from '../../models/routes';
import {
    EditUser,
    EditUserFormValues,
    ShippingMethod,
    Universe,
} from '../../types';

type PageProps = {
    user: EditUser;
    universes: Universe[];
    currentUniverse?: Universe | null;
    availableShippingMethods?: ShippingMethod[];
    errors?: Record<string, string>;
    saved_delivery_profiles?: {
        nova_poshta: SavedDeliveryProfile | null;
        ukrposhta: SavedDeliveryProfile | null;
    };
    telegram_bot?: TelegramBotSettingsData;
    notification_settings?: NotificationSetting[];
};

const EditProfile: FC = () => {
    const page = usePage<PageProps>();
    const {
        user,
        universes,
        currentUniverse,
        availableShippingMethods,
        saved_delivery_profiles,
        telegram_bot,
        notification_settings,
        errors: pageErrors,
    } = page.props;
    const requestedSection = new URLSearchParams(
        page.url.split('?')[1]?.split('#')[0] ?? '',
    ).get('section');
    const selectableMethods: ShippingMethod[] = availableShippingMethods ?? [
        'nova_poshta',
        'pickup',
    ];

    const { data, setData, patch, processing, errors, transform } =
        useForm<EditUserFormValues>({
            nickname: user.nickname ?? '',
            first_name: user.first_name ?? '',
            last_name: user.last_name ?? '',
            email: user.email ?? '',
            telegram_username: user.telegram_username ?? '',
            bio: user.bio ?? '',
            city: user.city ?? '',
            nova_poshta_city_ref: user.nova_poshta_city_ref ?? '',
            avatar: null,
        });

    const [isPersonalDataOpen, setIsPersonalDataOpen] = useState(
        Boolean(pageErrors?.city),
    );
    const [isPasswordSectionOpen, setIsPasswordSectionOpen] = useState(false);
    const [isDeleteProfileSectionOpen, setIsDeleteProfileSectionOpen] =
        useState(false);
    const [isDeliverySectionOpen, setIsDeliverySectionOpen] = useState(
        requestedSection === 'delivery',
    );
    const [isPreferredUniverseSectionOpen, setIsPreferredUniverseSectionOpen] =
        useState(false);
    const [isShippingMethodsSectionOpen, setIsShippingMethodsSectionOpen] =
        useState(false);
    const [shippingCityError, setShippingCityError] = useState<string>();
    const [isAvailabilitySectionOpen, setIsAvailabilitySectionOpen] = useState(
        Boolean(user.seller_paused),
    );
    const [isTelegramBotSectionOpen, setIsTelegramBotSectionOpen] =
        useState(false);
    const [
        isNotificationSettingsSectionOpen,
        setIsNotificationSettingsSectionOpen,
    ] = useState(false);
    const {
        data: shippingMethodsData,
        setData: setShippingMethodsData,
        patch: patchShippingMethods,
        processing: shippingMethodsProcessing,
    } = useForm<{
        user: {
            shipping_methods: ShippingMethod[];
            city: string;
            nova_poshta_city_ref: string;
        };
    }>({
        user: {
            shipping_methods: (user.shipping_methods ?? []).filter((method) =>
                selectableMethods.includes(method),
            ),
            city: user.city ?? '',
            nova_poshta_city_ref: user.nova_poshta_city_ref ?? '',
        },
    });

    const handleInputChange = (
        field: keyof EditUserFormValues,
        value: string | File | null,
    ) => {
        if (
            (field === 'city' || field === 'nova_poshta_city_ref') &&
            typeof value === 'string'
        ) {
            setShippingMethodsData((currentData) => ({
                ...currentData,
                user: {
                    ...currentData.user,
                    [field]: value,
                },
            }));
        }

        setData(field, value);
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        transform((data) => ({
            user: data,
        }));
        patch(Routes.ProfileEdit);
    };

    const handleShippingMethodsSave = () => {
        patchShippingMethods(Routes.ProfileEdit, {
            preserveScroll: true,
            onSuccess: () => setShippingCityError(undefined),
            onError: (formErrors) =>
                setShippingCityError(
                    typeof formErrors.city === 'string'
                        ? formErrors.city
                        : undefined,
                ),
        });
    };

    useEffect(() => {
        if (requestedSection !== 'delivery') return;

        setIsDeliverySectionOpen(true);
        const frameId = window.requestAnimationFrame(() => {
            document
                .getElementById('delivery-details')
                ?.scrollIntoView({ block: 'start' });
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [requestedSection]);

    useEffect(() => {
        if (pageErrors?.city) setIsPersonalDataOpen(true);
    }, [pageErrors?.city]);

    return (
        <div className="py-6">
            <div className="mb-6 flex flex-col">
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                    Редагування профілю
                </h1>
                {!user.email_confirmed && (
                    <div
                        className="mt-4 flex flex-col justify-between gap-4 rounded-lg border border-yellow-500 bg-yellow-100 p-4 text-yellow-700 sm:flex-row sm:items-center dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-200"
                        role="alert"
                    >
                        <div>
                            <p className="font-bold">Увага!</p>
                            <p>
                                Ваша електронна пошта не підтверджена. Будь
                                ласка, перевірте свою поштову скриньку.
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            className="bg-white whitespace-nowrap text-black hover:bg-yellow-200 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
                            onClick={() =>
                                router.post(Routes.UserConfirmation, {
                                    user: { email: user.email },
                                })
                            }
                        >
                            Надіслати повторно
                        </Button>
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-6">
                <PersonalDataForm
                    formData={data}
                    errors={errors}
                    onInputChange={handleInputChange}
                    onSubmit={handleSubmit}
                    processing={processing}
                    currentAvatarUrl={user.avatar}
                    cityRequired={(user.shipping_methods ?? []).includes(
                        'pickup',
                    )}
                    isOpen={isPersonalDataOpen}
                    onToggle={() => setIsPersonalDataOpen(!isPersonalDataOpen)}
                />

                <PasswordChangeForm
                    isOpen={isPasswordSectionOpen}
                    onToggle={() =>
                        setIsPasswordSectionOpen(!isPasswordSectionOpen)
                    }
                />

                <DeliveryProfilesForm
                    isOpen={isDeliverySectionOpen}
                    onToggle={() =>
                        setIsDeliverySectionOpen(!isDeliverySectionOpen)
                    }
                    availableMethods={selectableMethods}
                    novaPoshta={saved_delivery_profiles?.nova_poshta ?? null}
                    ukrposhta={saved_delivery_profiles?.ukrposhta ?? null}
                />

                <ShippingMethodsForm
                    isOpen={isShippingMethodsSectionOpen}
                    onToggle={() =>
                        setIsShippingMethodsSectionOpen(
                            !isShippingMethodsSectionOpen,
                        )
                    }
                    selectedMethods={shippingMethodsData.user.shipping_methods}
                    availableMethods={selectableMethods}
                    onChange={(methods) =>
                        setShippingMethodsData('user', {
                            ...shippingMethodsData.user,
                            shipping_methods: methods,
                        })
                    }
                    onSave={handleShippingMethodsSave}
                    processing={shippingMethodsProcessing}
                    error={pageErrors?.shipping_methods}
                    cityError={shippingCityError ?? pageErrors?.city}
                    city={shippingMethodsData.user.city}
                    cityRef={shippingMethodsData.user.nova_poshta_city_ref}
                    onCityChange={(city, cityRef) => {
                        setShippingMethodsData('user', {
                            ...shippingMethodsData.user,
                            city,
                            nova_poshta_city_ref: cityRef,
                        });
                        setData('city', city);
                        setData('nova_poshta_city_ref', cityRef);
                    }}
                />

                <SellerAvailabilityForm
                    user={user}
                    isOpen={isAvailabilitySectionOpen}
                    onToggle={() =>
                        setIsAvailabilitySectionOpen(!isAvailabilitySectionOpen)
                    }
                    error={pageErrors?.seller_availability}
                />

                <TelegramBotSettings
                    settings={telegram_bot}
                    isOpen={isTelegramBotSectionOpen}
                    onToggle={() =>
                        setIsTelegramBotSectionOpen(!isTelegramBotSectionOpen)
                    }
                />

                <NotificationSettingsForm
                    settings={notification_settings ?? []}
                    isOpen={isNotificationSettingsSectionOpen}
                    onToggle={() =>
                        setIsNotificationSettingsSectionOpen(
                            !isNotificationSettingsSectionOpen,
                        )
                    }
                    telegramConnected={Boolean(telegram_bot?.account)}
                    error={pageErrors?.notification_settings}
                />

                <PreferredUniverseForm
                    isOpen={isPreferredUniverseSectionOpen}
                    onToggle={() =>
                        setIsPreferredUniverseSectionOpen(
                            !isPreferredUniverseSectionOpen,
                        )
                    }
                    universes={universes}
                    currentUniverse={currentUniverse}
                />

                <DeleteProfileForm
                    isOpen={isDeleteProfileSectionOpen}
                    onToggle={() =>
                        setIsDeleteProfileSectionOpen(
                            !isDeleteProfileSectionOpen,
                        )
                    }
                />
            </div>
        </div>
    );
};

export default EditProfile;
